// src/pages/admin/ProjectsManage.js
import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Space, Popconfirm, message, Typography, Modal, Form, Input as AntInput, Select, Tag } from 'antd';
import { PlusOutlined, EditOutlined, EyeOutlined, StopOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { Option } = Select;

const ProjectsManage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [form] = Form.useForm();

  // Simulated API call to fetch projects
  const fetchProjects = async (page = 1, pageSize = 10, search = '') => {
    setLoading(true);
    try {
      const mockData = [
        {
          id: 1,
          title: 'AI Chatbot Development',
          description: 'Build an AI-powered chatbot for customer support',
          category_id: 1, // Assume category_id 1 is 'AI'
          difficulty: 'INTERMEDIATE',
          tech_stack: ['Python', 'TensorFlow', 'React'],
          country: 'Kenya',
          status: 'IN_PROGRESS',
          completion_percentage: 65.50,
          created_at: '2025-01-15T08:00:00Z',
          updated_at: '2025-03-07T14:30:00Z',
        },
        // Add more mock data as needed
      ].filter(project =>
        project.title.toLowerCase().includes(search.toLowerCase()) ||
        project.description.toLowerCase().includes(search.toLowerCase())
      );

      const start = (page - 1) * pageSize;
      const end = start + pageSize;
      const paginatedData = mockData.slice(start, end);

      setProjects(paginatedData);
      setPagination({
        current: page,
        pageSize: pageSize,
        total: mockData.length,
      });
    } catch (error) {
      message.error('Failed to fetch projects');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects(pagination.current, pagination.pageSize, searchText);
  }, [pagination.current, pagination.pageSize, searchText]);

  const columns = [
    { title: 'Id', dataIndex: 'id', key: 'id', sorter: (a, b) => a.id - b.id },
    { title: 'Title', dataIndex: 'title', key: 'title', sorter: (a, b) => a.title.localeCompare(b.title) },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    { title: 'Difficulty', dataIndex: 'difficulty', key: 'difficulty' },
    { title: 'Tech Stack', dataIndex: 'tech_stack', key: 'tech_stack', render: tech => tech.map((t, i) => <Tag key={i}>{t}</Tag>) },
    { title: 'Country', dataIndex: 'country', key: 'country' },
    { title: 'Status', dataIndex: 'status', key: 'status', filters: [
        { text: 'Proposed', value: 'PROPOSED' },
        { text: 'In Progress', value: 'IN_PROGRESS' },
        { text: 'Completed', value: 'COMPLETED' },
      ], onFilter: (value, record) => record.status === value },
    { title: 'Completion (%)', dataIndex: 'completion_percentage', key: 'completion_percentage', sorter: (a, b) => a.completion_percentage - b.completion_percentage },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EyeOutlined />} onClick={() => handleView(record)}>View</Button>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>Edit</Button>
          {record.status !== 'COMPLETED' && (
            <Popconfirm
              title="Are you sure to mark this project as completed?"
              onConfirm={() => handleComplete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button icon={<StopOutlined />} danger>Complete</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const handleCreate = () => {
    setCreateModalVisible(true);
  };

  const handleView = (project) => {
    setSelectedProject(project);
    setViewModalVisible(true);
  };

  const handleEdit = (project) => {
    setSelectedProject(project);
    form.setFieldsValue(project); // Pre-fill form with project data
    setEditModalVisible(true);
  };

  const handleComplete = async (id) => {
    try {
      const updatedProjects = projects.map(project =>
        project.id === id ? { ...project, status: 'COMPLETED', completion_percentage: 100, updated_at: new Date().toISOString() } : project
      );
      setProjects(updatedProjects);
      message.success('Project marked as completed successfully');
      fetchProjects(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error('Failed to complete project');
      console.error(error);
    }
  };

  const handleTableChange = (newPagination) => {
    fetchProjects(newPagination.current, newPagination.pageSize, searchText);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
  };

  const handleCreateSubmit = async (values) => {
    try {
      const newProject = {
        id: projects.length + 1,
        ...values,
        tech_stack: values.tech_stack ? values.tech_stack.split(',').map(tech => tech.trim()) : [],
        status: values.status || 'PROPOSED',
        completion_percentage: values.completion_percentage || 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setProjects([...projects, newProject]);
      message.success('Project created successfully');
      setCreateModalVisible(false);
      form.resetFields();
      fetchProjects(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error('Failed to create project');
      console.error(error);
    }
  };

  const handleEditSubmit = async (values) => {
    try {
      const updatedProjects = projects.map(project =>
        project.id === selectedProject.id ? {
          ...project,
          ...values,
          tech_stack: values.tech_stack.split(',').map(tech => tech.trim()),
          updated_at: new Date().toISOString(),
        } : project
      );
      setProjects(updatedProjects);
      message.success('Project updated successfully');
      setEditModalVisible(false);
      form.resetFields();
      fetchProjects(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error('Failed to update project');
      console.error(error);
    }
  };

  const projectFormFields = (
    <>
      <Form.Item name="title" label="Project Title" rules={[{ required: true, message: 'Please enter the project title' }]}>
        <AntInput placeholder="e.g., AI Chatbot Development" />
      </Form.Item>
      <Form.Item name="description" label="Description" rules={[{ required: true, message: 'Please enter a description' }]}>
        <AntInput.TextArea placeholder="e.g., Build an AI-powered chatbot" rows={4} />
      </Form.Item>
      <Form.Item name="category_id" label="Category ID" rules={[{ required: true, message: 'Please enter the category ID' }]}>
        <AntInput type="number" placeholder="e.g., 1 for AI" min={1} />
      </Form.Item>
      <Form.Item name="difficulty" label="Difficulty" rules={[{ required: true, message: 'Please select the difficulty' }]}>
        <Select placeholder="Select difficulty">
          <Option value="BEGINNER">Beginner</Option>
          <Option value="INTERMEDIATE">Intermediate</Option>
          <Option value="ADVANCED">Advanced</Option>
        </Select>
      </Form.Item>
      <Form.Item name="tech_stack" label="Tech Stack (comma-separated)" rules={[{ required: true, message: 'Please enter the tech stack' }]}>
        <AntInput placeholder="e.g., Python, TensorFlow, React" />
      </Form.Item>
      <Form.Item name="country" label="Country">
        <AntInput placeholder="e.g., Kenya" />
      </Form.Item>
      <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Please select the status' }]}>
        <Select placeholder="Select status">
          <Option value="PROPOSED">Proposed</Option>
          <Option value="IN_PROGRESS">In Progress</Option>
          <Option value="COMPLETED">Completed</Option>
        </Select>
      </Form.Item>
      <Form.Item
        name="completion_percentage"
        label="Completion Percentage"
        rules={[
          { required: true, message: 'Please enter the completion percentage' },
          { type: 'number', min: 0, max: 100, message: 'Must be between 0 and 100' },
        ]}
      >
        <AntInput type="number" placeholder="e.g., 65.50" min={0} max={100} step="0.01" />
      </Form.Item>
    </>
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={2} style={{ fontSize: '15px' }}>Manage Projects</Title>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Input.Search
            placeholder="Search projects by title or description"
            onSearch={handleSearch}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            enterButton
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Add New Project
          </Button>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={projects}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} projects`,
        }}
        onChange={handleTableChange}
      />

      {/* View Modal */}
      <Modal
        title="Project Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={<Button onClick={() => setViewModalVisible(false)}>Close</Button>}
        width={600}
      >
        {selectedProject && (
          <div>
            <p><strong>Id:</strong> {selectedProject.id}</p>
            <p><strong>Title:</strong> {selectedProject.title}</p>
            <p><strong>Description:</strong> {selectedProject.description}</p>
            <p><strong>Category ID:</strong> {selectedProject.category_id}</p>
            <p><strong>Difficulty:</strong> {selectedProject.difficulty}</p>
            <p><strong>Tech Stack:</strong> {selectedProject.tech_stack.map((tech, i) => <Tag key={i}>{tech}</Tag>)}</p>
            <p><strong>Country:</strong> {selectedProject.country || 'N/A'}</p>
            <p><strong>Status:</strong> {selectedProject.status}</p>
            <p><strong>Completion Percentage:</strong> {selectedProject.completion_percentage.toFixed(2)}%</p>
            <p><strong>Created At:</strong> {new Date(selectedProject.created_at).toLocaleString()}</p>
            <p><strong>Updated At:</strong> {new Date(selectedProject.updated_at).toLocaleString()}</p>
          </div>
        )}
      </Modal>

      {/* Create Modal */}
      <Modal
        title="Add New Project"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateSubmit}>
          {projectFormFields}
          <Form.Item>
            <Button type="primary" htmlType="submit">Create Project</Button>
            <Button style={{ marginLeft: 8 }} onClick={() => { setCreateModalVisible(false); form.resetFields(); }}>Cancel</Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Edit Project"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleEditSubmit}>
          {projectFormFields}
          <Form.Item>
            <Button type="primary" htmlType="submit">Update Project</Button>
            <Button style={{ marginLeft: 8 }} onClick={() => { setEditModalVisible(false); form.resetFields(); }}>Cancel</Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProjectsManage;